import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiService from "../services/ApiService";
import OAuthProviderButtons from "./OAuthProviderButtons";

const TERMS_URL = "https://orkaapps.com/links/terms";
const PRIVACY_URL = "https://orkaapps.com/links/privacy";
const PASSWORD_RULES = "8+ characters with upper and lower case letters and a number";

const PERKS = [
  "Connect Facebook, Instagram & Google",
  "Campaign-level performance breakdown",
  "Export reports as CSV or PDF",
  "Same Orka login as OrkaStores & WappGrow",
];

const signupSchema = Yup.object({
  firstName: Yup.string().trim().required("Name is required").max(50),
  email: Yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobile: Yup.string()
    .trim()
    .required("Mobile number is required")
    .matches(/^[0-9]{10,15}$/, "Enter a valid mobile number (10–15 digits, with country code if needed)"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "At least 8 characters")
    .matches(/[a-z]/, "Include a lowercase letter")
    .matches(/[A-Z]/, "Include an uppercase letter")
    .matches(/[0-9]/, "Include a number"),
});

const otpSchema = Yup.object({
  otp: Yup.string()
    .trim()
    .required("Enter the OTP")
    .matches(/^[0-9]{4}$/, "OTP must be 4 digits"),
});

export function SignUpPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [otpView, setOtpView] = useState(false);
  const [mobileVerificationId, setMobileVerificationId] = useState(null);
  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    document.title = "Sign up · Orka Analytics";
  }, []);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
      <Box sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: 460, flexShrink: 0,
        bgcolor: "#0F172A", p: 5,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,91,214,0.3) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(91,91,214,0.5)" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#F1F5F9", lineHeight: 1.2 }}>Orka Analytics</Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "#475569", letterSpacing: "0.04em" }}>Marketing platform</Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative" }}>
          <Typography sx={{ fontSize: "1.875rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1.25, letterSpacing: "-0.03em", mb: 2 }}>
            Start making{" "}
            <Box component="span" sx={{ background: "linear-gradient(90deg, #818CF8 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              smarter decisions.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.75, mb: 4 }}>
            Everything you need to understand your marketing performance — set up in minutes.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {PERKS.map((perk) => (
              <Box key={perk} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#818CF8", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.875rem", color: "#94A3B8" }}>{perk}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
            Already have an Orka account?{" "}
            <Link component={RouterLink} to="/signin" sx={{ color: "#818CF8", textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
              Sign in →
            </Link>
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0, width: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        bgcolor: "#F0F2F7", px: { xs: 2, sm: 5 }, py: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>Orka Analytics</Typography>
        </Box>

        <Box sx={{
          bgcolor: "#fff", borderRadius: 3, border: "1px solid #E8EDF5",
          px: { xs: 2, sm: 4.5 }, py: { xs: 3, sm: 4.5 },
          width: "100%", maxWidth: 420, minWidth: 0, boxSizing: "border-box",
          boxShadow: "0px 4px 24px rgba(15,23,42,0.07)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0F172A", mb: 0.75 }}>
            {otpView ? "Verify OTP" : "Create account"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>
            {otpView
              ? `Enter the OTP sent to ${formValues?.mobile || "your mobile"}`
              : "Create one Orka account for Stores, WappGrow, and Analytics."}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>{error}</Alert>}

          {!otpView ? (
            <Formik
              initialValues={{ firstName: "", email: "", mobile: "", password: "" }}
              validationSchema={signupSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                try {
                  const res = await ApiService.signupVerificationCode(
                    values.mobile.trim(),
                    values.firstName.trim(),
                  );
                  const id = res?.id?.mobileVerificationId
                    || (typeof res?.id === "string" ? res.id : null);
                  if (!id) throw new Error("Unexpected verification response");
                  setMobileVerificationId(id);
                  setFormValues({
                    firstName: values.firstName.trim(),
                    lastName: "",
                    email: values.email.trim(),
                    mobile: values.mobile.trim(),
                    password: values.password,
                  });
                  setOtpView(true);
                } catch (e) {
                  setError(e?.message || "Could not send OTP");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <TextField fullWidth name="firstName" label="Name" margin="normal"
                    value={values.firstName} onChange={handleChange} onBlur={handleBlur}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName} autoFocus />
                  <TextField fullWidth name="email" label="Email" type="email" margin="normal"
                    value={values.email} onChange={handleChange} onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    autoComplete="email" />
                  <TextField fullWidth name="mobile" label="Mobile" margin="normal"
                    value={values.mobile} onChange={handleChange} onBlur={handleBlur}
                    error={touched.mobile && Boolean(errors.mobile)}
                    helperText={(touched.mobile && errors.mobile) || "Digits only, with country code if needed"}
                    inputProps={{ inputMode: "numeric", maxLength: 15 }} />
                  <TextField fullWidth name="password" label="Password" type={showPassword ? "text" : "password"} margin="normal"
                    value={values.password} onChange={handleChange} onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={(touched.password && errors.password) || PASSWORD_RULES}
                    FormHelperTextProps={{ sx: { wordBreak: "break-word" } }}
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}>
                          {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )}} />
                  <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                    {isSubmitting ? "Sending OTP…" : "Create account"}
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <Formik
              initialValues={{ otp: "" }}
              validationSchema={otpSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                try {
                  await ApiService.signup({
                    firstName: formValues.firstName,
                    lastName: formValues.lastName,
                    email: formValues.email,
                    mobile: formValues.mobile,
                    password: formValues.password,
                    mobileVerificationId,
                    mobileVerificationCode: values.otp.trim(),
                  });
                  navigate("/", { replace: true });
                } catch (e) {
                  setError(e?.message || "Sign up failed");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <TextField fullWidth name="otp" label="4-digit OTP" margin="normal"
                    value={values.otp} onChange={handleChange} onBlur={handleBlur}
                    error={touched.otp && Boolean(errors.otp)}
                    helperText={touched.otp && errors.otp}
                    inputProps={{ inputMode: "numeric", maxLength: 4 }} autoFocus />
                  <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                    {isSubmitting ? "Creating account…" : "Verify and create account"}
                  </Button>
                  <Button fullWidth variant="text" sx={{ mt: 1.5, color: "#64748B" }}
                    onClick={() => {
                      setOtpView(false);
                      setMobileVerificationId(null);
                      setError(null);
                    }}>
                    Back
                  </Button>
                </Form>
              )}
            </Formik>
          )}

          {!otpView && (
            <OAuthProviderButtons
              onAuthenticated={() => navigate("/", { replace: true })}
              onError={(message) => setError(message)}
            />
          )}

          <Typography variant="caption" sx={{ color: "#94A3B8", mt: 2.5, display: "block", textAlign: "center", lineHeight: 1.6 }}>
            By continuing, you agree to our{" "}
            <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#5B5BD6" }}>
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#5B5BD6" }}>
              Privacy Policy
            </Link>
            .
          </Typography>

          <Typography variant="body2" sx={{ color: "#64748B", mt: 2.5, textAlign: "center" }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/signin" sx={{ color: "#5B5BD6", fontWeight: 600, textDecoration: "none" }}>
              Sign in
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
